import { array, boolean, number, option, ord, record, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';

import { AgeCategoryClassification } from '@mews-distributor/data-access';
import { getStringProp, values } from '@mews/fp';

import { getEntities } from '../getEntities';
import { AgeCategory } from './types';

const getAgeCategoriesEntity = createSelector(getEntities, (entities) => entities.ageCategories);

export const getAgeCategoriesData = createSelector(
    getAgeCategoriesEntity,
    (ageCategoriesEntity) => ageCategoriesEntity.data
);

export const getAgeCategories = createSelector(getAgeCategoriesData, values);

const getAgeCategory = createSelector(
    getStringProp('ageCategoryId'),
    getAgeCategoriesData,
    (ageCategoryId, ageCategories) => pipe(ageCategories, record.lookup(ageCategoryId))
);

export const getAgeCategoryName = createSelector(
    getAgeCategory,
    option.map(({ name }) => name)
);

export const getAgeCategoryMinimalAge = createSelector(
    getAgeCategory,
    option.chainNullableK(({ minimalAge }) => minimalAge)
);

export const getAgeCategoryMaximalAge = createSelector(
    getAgeCategory,
    option.chainNullableK(({ maximalAge }) => maximalAge)
);

export const getAgeCategoryClassification = createSelector(
    getAgeCategory,
    flow(
        option.map(({ classification }) => classification),
        option.getOrElse(() => AgeCategoryClassification.Adult)
    )
);

export const isAgeCategoryAdultClassification = createSelector(getAgeCategoryClassification, (classification) =>
    string.Eq.equals(AgeCategoryClassification.Adult, classification)
);

const byIsDefault = pipe(
    boolean.Ord,
    ord.contramap(({ isDefault }: AgeCategory) => isDefault),
    ord.reverse
);

export const isDefaultAgeCategory = createSelector(getAgeCategory, (ageCategory) =>
    pipe(
        ageCategory,
        option.map(({ isDefault }) => isDefault),
        option.getOrElse(() => false)
    )
);

const byMinimalAge = pipe(
    number.Ord,
    ord.contramap(({ minimalAge }: AgeCategory) => minimalAge ?? Number.MAX_SAFE_INTEGER),
    ord.reverse
);

const byMaximalAge = pipe(
    number.Ord,
    ord.contramap(({ minimalAge }: AgeCategory) => minimalAge ?? Number.MAX_SAFE_INTEGER)
);

export const getSortedAgeCategories = createSelector(
    getStringProp('serviceId'),
    getAgeCategoriesData,
    (propServiceId, ageCategories) =>
        pipe(
            ageCategories,
            record.filter(({ serviceId }) => string.Eq.equals(serviceId, propServiceId)),
            record.collect((_key, value) => value),
            array.sortBy([byIsDefault, byMinimalAge])
        )
);

export const getSortedAgeCategoryIds = createSelector(
    getSortedAgeCategories,
    array.map(({ id }) => id)
);

export const getSortedAgeCategoryWithoutChildrenIds = createSelector(
    getSortedAgeCategories,
    array.filterMap(({ id, classification }) =>
        classification === AgeCategoryClassification.Child ? option.none : option.some(id)
    )
);
interface UniqueClassificationAgeCategories {
    adultAgeCategoryId: string | null;
    childAgeCategoryId: string | null;
}

export const getUniqueAgeCategoriesPerClassification = createSelector(
    getSortedAgeCategories,
    flow(
        array.sortBy([byMaximalAge]),
        array.reduce<AgeCategory, UniqueClassificationAgeCategories>(
            { adultAgeCategoryId: null, childAgeCategoryId: null },
            (uniqueAgeCategories, { classification, isDefault, id }) => {
                if (isDefault) {
                    return { ...uniqueAgeCategories, adultAgeCategoryId: id };
                }

                if (classification === AgeCategoryClassification.Child) {
                    return { ...uniqueAgeCategories, childAgeCategoryId: id };
                }

                return uniqueAgeCategories;
            }
        )
    )
);
